import type { MapApiDialog } from './types';
import { normalizeMoneyInput, parseCanonicalMoney } from '../../../core/money/money';

/** Application-relative route that accepts the validated payment query. */
const PAYMENT_PAGE_PATH = '/payment';
/** Document event used to delegate authentication UI to the shared site header. */
const SITE_AUTH_OPEN_LOGIN_EVENT = 'site-auth:open-login';

/** Validation state and canonical query value derived from the dialog input. */
interface PaymentAmountParseResult {
  /** Whether the input can be submitted as a positive payment. */
  isValid: boolean;
  /** Canonical positive two-decimal amount, or null for invalid input. */
  value: string | null;
}

/**
 * Parses the user-entered ruble amount and validates that it matches the payment API contract.
 *
 * @param rawAmount User-entered amount string from the dialog input.
 * @returns Validation state and canonical positive two-decimal amount, or null when invalid.
 */
function parsePaymentAmount(rawAmount: string): PaymentAmountParseResult {
  const amountValue = normalizeMoneyInput(rawAmount);

  return {
    isValid: amountValue !== null,
    value: amountValue,
  };
}

/**
 * Builds the `/payment` URL expected by the existing payment page and QR generator.
 *
 * @param params Field number, validated amount and machine-readable payment type.
 * @returns Absolute application-relative payment URL with query params.
 */
function buildPaymentUrl(params: {
  fieldNumber: string;
  amount: string;
  submitType: string;
}): string {
  const searchParams = new URLSearchParams();
  searchParams.set('field_number', params.fieldNumber);
  searchParams.set('amount', params.amount);
  searchParams.set('type', params.submitType);

  return `${PAYMENT_PAGE_PATH}?${searchParams.toString()}`;
}

/**
 * Creates the Radix-style close icon used by the payment dialog button.
 *
 * @returns Fresh close icon SVG with `currentColor` fill.
 */
function createCloseIconElement(): SVGSVGElement {
  const closeIconElement = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'svg',
  ) as SVGSVGElement;
  closeIconElement.setAttribute('width', '15');
  closeIconElement.setAttribute('height', '15');
  closeIconElement.setAttribute('viewBox', '0 0 15 15');
  closeIconElement.setAttribute('fill', 'none');
  closeIconElement.setAttribute('aria-hidden', 'true');

  const closeIconPathElement = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'path',
  ) as SVGPathElement;
  closeIconPathElement.setAttribute(
    'd',
    'M12.8536 2.85355C13.0488 2.65829 13.0488 2.34171 12.8536 2.14645C12.6583 1.95118 12.3417 1.95118 12.1464 2.14645L7.5 6.79289L2.85355 2.14645C2.65829 1.95118 2.34171 1.95118 2.14645 2.14645C1.95118 2.34171 1.95118 2.65829 2.14645 2.85355L6.79289 7.5L2.14645 12.1464C1.95118 12.3417 1.95118 12.6583 2.14645 12.8536C2.34171 13.0488 2.65829 13.0488 2.85355 12.8536L7.5 8.20711L12.1464 12.8536C12.3417 13.0488 12.6583 13.0488 12.8536 12.8536C13.0488 12.6583 13.0488 12.3417 12.8536 12.1464L8.20711 7.5L12.8536 2.85355Z',
  );
  closeIconPathElement.setAttribute('fill', 'currentColor');
  closeIconPathElement.setAttribute('fill-rule', 'evenodd');
  closeIconPathElement.setAttribute('clip-rule', 'evenodd');
  closeIconElement.append(closeIconPathElement);

  return closeIconElement;
}

/**
 * Renders one structured payment dialog into the prepared dialog content host.
 *
 * @param params Dialog host, field number, API payload and close callback.
 */
export function renderDialogContent(params: {
  dialogContent: HTMLElement;
  fieldNumber: string;
  dialogData: MapApiDialog;
  closeDialog: () => void;
}): void {
  const { dialogContent, fieldNumber, dialogData, closeDialog } = params;

  dialogContent.textContent = '';

  const panelElement = document.createElement('section');
  panelElement.className =
    'relative w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl transition duration-200 scale-95 opacity-0';
  panelElement.setAttribute('role', 'dialog');
  panelElement.setAttribute('aria-modal', 'true');
  panelElement.setAttribute('aria-labelledby', 'map-payment-dialog-title');
  panelElement.setAttribute('aria-describedby', 'map-payment-dialog-description');

  const closeButtonElement = document.createElement('button');
  closeButtonElement.className =
    'absolute right-4 top-4 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white p-0 text-slate-500 transition hover:border-slate-300 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30';
  closeButtonElement.type = 'button';
  closeButtonElement.setAttribute('aria-label', 'Закрыть окно оплаты');
  closeButtonElement.append(createCloseIconElement());
  closeButtonElement.addEventListener('click', () => {
    closeDialog();
  });

  const titleElement = document.createElement('h3');
  titleElement.id = 'map-payment-dialog-title';
  titleElement.className = 'pr-12 text-xl font-semibold text-slate-950';
  titleElement.textContent = dialogData.title;

  const descriptionElement = document.createElement('p');
  descriptionElement.id = 'map-payment-dialog-description';
  descriptionElement.className = 'mt-3 text-sm leading-6 text-slate-600';
  descriptionElement.textContent = dialogData.description;

  const fieldMetaElement = document.createElement('p');
  fieldMetaElement.className =
    'mt-4 text-xs font-semibold uppercase tracking-[0.15em] text-slate-700';
  fieldMetaElement.textContent = `Участок № ${fieldNumber}`;

  const cadastralElement = document.createElement('p');
  cadastralElement.className = 'mt-3 text-sm text-slate-500';
  cadastralElement.textContent = dialogData.cadastralText;

  const areaElement = document.createElement('p');
  areaElement.className = 'mt-2 text-sm text-slate-500';
  areaElement.textContent = dialogData.areaText;

  const headerElements = [
    closeButtonElement,
    titleElement,
    descriptionElement,
    fieldMetaElement,
    cadastralElement,
    areaElement,
  ];

  if (dialogData.overallDebtText !== null) {
    const overallDebtElement = document.createElement('p');
    overallDebtElement.className =
      'mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700';
    overallDebtElement.textContent = dialogData.overallDebtText;
    headerElements.push(overallDebtElement);
  }

  const formElement = document.createElement('form');
  formElement.className = 'mt-5 space-y-4';
  formElement.noValidate = true;

  const amountLabelElement = document.createElement('label');
  amountLabelElement.className = 'block';

  const amountLabelTextElement = document.createElement('span');
  amountLabelTextElement.className =
    'text-xs font-semibold uppercase tracking-[0.15em] text-slate-500';
  amountLabelTextElement.textContent = 'Сумма платежа, руб.';

  const amountInputElement = document.createElement('input');
  amountInputElement.className =
    'mt-2 block w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-base text-slate-950 shadow-sm transition placeholder:text-slate-400 focus-visible:border-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/20';
  amountInputElement.type = 'text';
  amountInputElement.inputMode = 'decimal';
  amountInputElement.placeholder = 'Введите сумму';
  amountInputElement.autocomplete = 'off';
  amountInputElement.spellcheck = false;
  amountInputElement.setAttribute(
    'aria-describedby',
    'map-payment-dialog-amount-hint map-payment-dialog-amount-error',
  );
  const payableTotalKopecks = parseCanonicalMoney(dialogData.payableTotalAmount);
  amountInputElement.value =
    payableTotalKopecks !== null && payableTotalKopecks > 0
      ? dialogData.payableTotalAmount.replace('.', ',')
      : '';

  amountLabelElement.append(amountLabelTextElement, amountInputElement);

  const amountHintElement = document.createElement('p');
  amountHintElement.id = 'map-payment-dialog-amount-hint';
  amountHintElement.className = 'mt-2 text-xs text-slate-600';
  amountHintElement.textContent =
    'Введите сумму в рублях. Копейки отделяйте запятой и указывайте двумя цифрами, например: 340,02.';

  const amountErrorElement = document.createElement('p');
  amountErrorElement.id = 'map-payment-dialog-amount-error';
  amountErrorElement.className = 'hidden text-sm font-medium text-rose-600';
  amountErrorElement.textContent =
    'Введите положительную сумму. Копейки отделяйте запятой и указывайте ровно двумя цифрами.';

  const actionsElement = document.createElement('div');
  actionsElement.className = 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end';

  const cancelButtonElement = document.createElement('button');
  cancelButtonElement.className =
    'inline-flex cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/30';
  cancelButtonElement.type = 'button';
  cancelButtonElement.textContent = 'Отмена';
  cancelButtonElement.addEventListener('click', () => {
    closeDialog();
  });

  const submitButtonElement = document.createElement('button');
  submitButtonElement.className =
    'inline-flex cursor-pointer items-center justify-center rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30 disabled:cursor-not-allowed disabled:bg-slate-300';
  submitButtonElement.type = 'submit';
  submitButtonElement.textContent = 'Оплатить';

  actionsElement.append(cancelButtonElement, submitButtonElement);

  /**
   * Shows or clears the submitted amount error without validating the current draft.
   *
   * @param isInvalid Whether the latest submit attempt failed amount validation.
   * @returns Nothing.
   */
  const renderAmountValidationError = (isInvalid: boolean): void => {
    amountErrorElement.classList.toggle('hidden', !isInvalid);
    amountInputElement.setAttribute('aria-invalid', isInvalid ? 'true' : 'false');
  };

  /**
   * Enables payment submission as soon as the user has entered a non-blank draft.
   *
   * Full money validation intentionally remains deferred until form submission so
   * incomplete decimal input does not resize the dialog while the user is typing.
   *
   * @returns Nothing.
   */
  const updateSubmitAvailability = (): void => {
    submitButtonElement.disabled = amountInputElement.value.trim() === '';
  };

  amountInputElement.addEventListener('input', () => {
    renderAmountValidationError(false);
    updateSubmitAvailability();
  });

  formElement.addEventListener('submit', (event) => {
    event.preventDefault();

    const parsedAmount = parsePaymentAmount(amountInputElement.value);
    renderAmountValidationError(!parsedAmount.isValid);
    if (!parsedAmount.isValid || parsedAmount.value === null) {
      amountInputElement.focus();
      return;
    }

    const paymentUrl = buildPaymentUrl({
      fieldNumber,
      amount: parsedAmount.value,
      submitType: dialogData.submitType,
    });

    const paymentWindow = window.open(paymentUrl, '_blank', 'noopener');
    if (paymentWindow !== null) {
      closeDialog();
    }
  });

  formElement.append(amountLabelElement, amountHintElement, amountErrorElement, actionsElement);

  panelElement.append(...headerElements, formElement);

  dialogContent.append(panelElement);
  updateSubmitAvailability();
  amountInputElement.focus();
  amountInputElement.select();
}

/**
 * Renders auth-required dialog for restricted camera click by guest users.
 *
 * @param params Dialog host and close callback from the map dialog controller.
 */
export function renderAuthRequiredDialogContent(params: {
  dialogContent: HTMLElement;
  closeDialog: () => void;
}): void {
  const { dialogContent, closeDialog } = params;

  dialogContent.textContent = '';

  const panelElement = document.createElement('section');
  panelElement.className =
    'relative w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl transition duration-200 scale-95 opacity-0';
  panelElement.setAttribute('role', 'dialog');
  panelElement.setAttribute('aria-modal', 'true');
  panelElement.setAttribute('aria-labelledby', 'map-auth-required-dialog-title');
  panelElement.setAttribute('aria-describedby', 'map-auth-required-dialog-description');

  const closeButtonElement = document.createElement('button');
  closeButtonElement.className =
    'absolute right-4 top-4 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white p-0 text-slate-500 transition hover:border-slate-300 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30';
  closeButtonElement.type = 'button';
  closeButtonElement.setAttribute('aria-label', 'Закрыть окно ограничения доступа');
  closeButtonElement.append(createCloseIconElement());
  closeButtonElement.addEventListener('click', () => {
    closeDialog();
  });

  const titleElement = document.createElement('h3');
  titleElement.id = 'map-auth-required-dialog-title';
  titleElement.className = 'pr-12 text-xl font-semibold text-slate-950';
  titleElement.textContent = 'Доступ к камере ограничен';

  const descriptionElement = document.createElement('p');
  descriptionElement.id = 'map-auth-required-dialog-description';
  descriptionElement.className = 'mt-3 text-sm leading-6 text-slate-600';
  descriptionElement.textContent =
    'Данная камера доступна только пользователям, авторизованным на сайте. Для получения доступа к камере необходимо войти на сайт с имеющейся учетной записью собственника участка в СНТ или зарегистрироваться';

  const actionsElement = document.createElement('div');
  actionsElement.className = 'mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end';

  const secondaryButtonElement = document.createElement('button');
  secondaryButtonElement.className =
    'inline-flex cursor-pointer items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300';
  secondaryButtonElement.type = 'button';
  secondaryButtonElement.textContent = 'Закрыть';
  secondaryButtonElement.addEventListener('click', () => {
    closeDialog();
  });

  const primaryButtonElement = document.createElement('button');
  primaryButtonElement.className =
    'inline-flex cursor-pointer items-center justify-center rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40';
  primaryButtonElement.type = 'button';
  primaryButtonElement.textContent = 'Войти или Зарегистрироваться';
  primaryButtonElement.addEventListener('click', () => {
    closeDialog();
    window.dispatchEvent(new CustomEvent(SITE_AUTH_OPEN_LOGIN_EVENT));
  });

  actionsElement.append(secondaryButtonElement, primaryButtonElement);
  panelElement.append(closeButtonElement, titleElement, descriptionElement, actionsElement);
  dialogContent.append(panelElement);
}
